/**
 * VariationWizard.tsx
 *
 * A 3-step wizard that generates individual product SKUs for every combination
 * of variation attributes and bulk-inserts them into inventory_products.
 *
 * Step 1 — Base Details
 *   Base name, base SKU prefix, base price, base cost (COGS), product group.
 *
 * Step 2 — Attributes
 *   Load variation types from inventory_variation_types.
 *   For each chosen type, the user enters comma-separated values.
 *   e.g.  Color: "אדום, ירוק, כחול"   Size: "S, M, L, XL"
 *
 * Step 3 — Preview & Tweak
 *   Cartesian product of all attribute values → one row per SKU.
 *   Auto-generated Name  = "{baseName} - {val1} - {val2} - …"
 *   Auto-generated SKU   = "{baseSku}-{VAL1}-{VAL2}-…" (uppercased, spaces→-)
 *   User can tweak any SKU or price cell inline before committing.
 *   "צור X מוצרים" → supabase.from('inventory_products').insert(rows[])
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Layers,
  Tag,
  Settings2,
  Table2,
  Folder,
  TrendingDown,
  ChevronDown,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  allocateSequentialSkus,
  type CompanySkuSettings,
} from '@/pages/inventory/SkuSettingsTab';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VariationType {
  id:   string;
  name: string;
}

/** One attribute row in Step 2 */
interface AttributeRow {
  typeId:   string;   // FK → inventory_variation_types.id
  typeName: string;   // display label
  rawInput: string;   // comma-separated user input
}

/** One generated SKU row in the preview table */
export interface GeneratedRow {
  /** Stable index key for React — never saved to DB */
  _key:    number;
  name:    string;
  sku:     string;
  price:   number;
  /** The combination labels, e.g. ["אדום", "M"] */
  combo:   string[];
}

/** Props exposed to the parent page */
export interface VariationWizardProps {
  companyId:    string;
  onClose:      () => void;
  onCreated:    (count: number) => void;
  /** Pre-flattened group options from the parent page */
  groupOptions: { id: string; label: string }[];
  /** Company SKU settings — drives sequential vs smart mode */
  skuSettings:  CompanySkuSettings | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Cartesian product of arrays of strings. */
function cartesian(arrays: string[][]): string[][] {
  if (arrays.length === 0) return [[]];
  return arrays.reduce<string[][]>(
    (acc, curr) => acc.flatMap(a => curr.map(c => [...a, c])),
    [[]],
  );
}

/**
 * Convert a variation value to a SKU-safe segment.
 * "Dark Blue" → "DARK-BLUE",  "XL" → "XL"
 */
function toSkuSegment(val: string): string {
  return val.trim().toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9\u05D0-\u05EA-]/g, '');
}

/**
 * Parse a comma-separated string into trimmed, non-empty value tokens.
 * "אדום, ירוק ,  " → ["אדום", "ירוק"]
 */
function parseValues(raw: string): string[] {
  return raw
    .split(',')
    .map(v => v.trim())
    .filter(v => v.length > 0);
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = [
  { label: 'פרטי בסיס',   icon: <Tag      className="w-3.5 h-3.5" /> },
  { label: 'מאפיינים',    icon: <Settings2 className="w-3.5 h-3.5" /> },
  { label: 'תצוגה מקדימה', icon: <Table2    className="w-3.5 h-3.5" /> },
];

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-0 mb-1">
      {STEPS.map((s, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors
            ${i === step
              ? 'bg-orange-100 text-orange-700'
              : i < step
                ? 'bg-green-50 text-green-600'
                : 'bg-gray-50 text-gray-400'}`}
          >
            {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.icon}
            <span className="hidden sm:inline">{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px w-4 flex-shrink-0 mx-1 ${i < step ? 'bg-green-300' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export function VariationWizard({
  companyId,
  onClose,
  onCreated,
  groupOptions,
  skuSettings,
}: VariationWizardProps) {

  const isSequential = (skuSettings?.sku_generation_method ?? 'sequential') === 'sequential';

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(0);    // 0 = base, 1 = attrs, 2 = preview

  // ── Step 1: Base details ──────────────────────────────────────────────────
  const [baseName,    setBaseName]    = useState('');
  // baseSku is only relevant in 'smart' mode; in 'sequential' it is auto-assigned
  const [baseSku,     setBaseSku]     = useState('');
  const [basePrice,   setBasePrice]   = useState<number>(0);
  const MAX_GROUPS_WIZARD = 6;
  const [baseCost,    setBaseCost]    = useState<number | null>(null);
  const [groupIds,    setGroupIds]    = useState<string[]>([]);
  const [step1Error,  setStep1Error]  = useState<string | null>(null);

  // ── Step 2: Variation types ───────────────────────────────────────────────
  const [varTypes,       setVarTypes]       = useState<VariationType[]>([]);
  const [loadingTypes,   setLoadingTypes]   = useState(false);
  const [loadTypesError, setLoadTypesError] = useState<string | null>(null);
  /** The attribute rows the user has configured */
  const [attrRows,    setAttrRows]    = useState<AttributeRow[]>([]);
  const [step2Error,  setStep2Error]  = useState<string | null>(null);

  // ── Step 3: Preview ───────────────────────────────────────────────────────
  const [previewRows, setPreviewRows] = useState<GeneratedRow[]>([]);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState<string | null>(null);

  // ── Load variation types when entering step 2 ─────────────────────────────
  const fetchVarTypes = useCallback(async () => {
    setLoadingTypes(true); setLoadTypesError(null);
    const { data, error } = await supabase
      .from('inventory_variation_types')
      .select('id, name')
      .eq('company_id', companyId)
      .order('name');
    if (error) { setLoadTypesError(error.message); }
    else       { setVarTypes(data ?? []); }
    setLoadingTypes(false);
  }, [companyId]);

  useEffect(() => {
    if (step === 1) fetchVarTypes();
  }, [step, fetchVarTypes]);

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [saving, onClose]);

  // ─────────────────────────────────────────────────────────────────────────
  // Step 1 helpers
  // ─────────────────────────────────────────────────────────────────────────

  const validateStep1 = (): string | null => {
    if (!baseName.trim()) return 'שם הבסיס הוא שדה חובה';
    if (!isSequential && !baseSku.trim())  return 'מקט הבסיס הוא שדה חובה';
    if (basePrice < 0)    return 'מחיר לא יכול להיות שלילי';
    return null;
  };

  const goToStep2 = () => {
    const err = validateStep1();
    if (err) { setStep1Error(err); return; }
    setStep1Error(null);
    setStep(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Step 2 helpers
  // ─────────────────────────────────────────────────────────────────────────

  const addAttrRow = (type: VariationType) => {
    // Don't add duplicates
    if (attrRows.some(r => r.typeId === type.id)) return;
    setAttrRows(prev => [...prev, { typeId: type.id, typeName: type.name, rawInput: '' }]);
  };

  const updateAttrInput = (typeId: string, value: string) => {
    setAttrRows(prev => prev.map(r => r.typeId === typeId ? { ...r, rawInput: value } : r));
  };

  const removeAttrRow = (typeId: string) => {
    setAttrRows(prev => prev.filter(r => r.typeId !== typeId));
  };

  /** Move an attribute up/down in the list (changes combination order) */
  const moveAttr = (typeId: string, dir: -1 | 1) => {
    setAttrRows(prev => {
      const idx = prev.findIndex(r => r.typeId === typeId);
      if (idx === -1) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx]!, next[swap]!] = [next[swap]!, next[idx]!];
      return next;
    });
  };

  const validateStep2 = (): string | null => {
    if (attrRows.length === 0) return 'הוסף לפחות מאפיין אחד עם ערכים';
    for (const r of attrRows) {
      if (parseValues(r.rawInput).length === 0) {
        return `הוסף ערכים עבור "${r.typeName}" (מופרדים בפסיקים)`;
      }
    }
    return null;
  };

  // ── Build the preview (memoised from attrRows) ────────────────────────────

  const generatedRows = useMemo<GeneratedRow[]>(() => {
    const parsedArrays = attrRows
      .map(r => parseValues(r.rawInput))
      .filter(arr => arr.length > 0);
    if (parsedArrays.length === 0) return [];

    const combos = cartesian(parsedArrays);
    return combos.map((combo, idx) => {
      const suffix = combo.map(toSkuSegment).join('-');
      // For sequential mode: SKU will be assigned on save, show placeholder
      const skuPreview = isSequential
        ? `SEQ-${String(idx + 1).padStart(4, '0')}` // placeholder — replaced on save
        : `${baseSku.trim().toUpperCase()}-${suffix}`;
      return {
        _key:  idx,
        name:  `${baseName.trim()} - ${combo.join(' - ')}`,
        sku:   skuPreview,
        price: basePrice,
        combo,
      };
    });
  }, [attrRows, baseName, baseSku, basePrice, isSequential]);

  const goToStep3 = () => {
    const err = validateStep2();
    if (err) { setStep2Error(err); return; }
    setStep2Error(null);
    // Initialise preview rows (user-editable copy)
    setPreviewRows(generatedRows.map(r => ({ ...r })));
    setSaveError(null);
    setStep(2);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Step 3 helpers — inline editing
  // ─────────────────────────────────────────────────────────────────────────

  const updatePreviewField = (key: number, field: 'name' | 'sku' | 'price', value: string) => {
    setPreviewRows(prev => prev.map(r =>
      r._key === key
        ? { ...r, [field]: field === 'price' ? (parseFloat(value) || 0) : value }
        : r,
    ));
  };

  // ── Validate preview before insert ────────────────────────────────────────

  const validatePreview = (): string | null => {
    const skus = new Set<string>();
    for (const r of previewRows) {
      if (!r.name.trim()) return `שם ריק בשורה "${r.sku}"`;
      if (!r.sku.trim())  return `מקט ריק בשורה "${r.name}"`;
      if (skus.has(r.sku.trim())) return `מקט כפול: "${r.sku}"`;
      skus.add(r.sku.trim());
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Bulk Insert
  // ─────────────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    // For smart mode, validate SKUs (sequential are assigned below)
    if (!isSequential) {
      const err = validatePreview();
      if (err) { setSaveError(err); return; }
    } else {
      // Still validate names are non-empty
      for (const r of previewRows) {
        if (!r.name.trim()) { setSaveError(`שם ריק בשורה ${r._key + 1}`); return; }
      }
    }

    setSaving(true); setSaveError(null);

    try {
      let finalSkus: string[];

      if (isSequential) {
        // Allocate a block of consecutive SKUs
        finalSkus = await allocateSequentialSkus(companyId, previewRows.length);
      } else {
        finalSkus = previewRows.map(r => r.sku.trim());
      }

      const rows = previewRows.map((r, idx) => ({
        company_id: companyId,
        sku:        finalSkus[idx]!,
        name:       r.name.trim(),
        price:      r.price,
        cost:       baseCost,
        image_url:  null,
      }));

      const { data: inserted, error } = await supabase
        .from('inventory_products')
        .insert(rows)
        .select('id');
      if (error) throw error;

      // Insert junction-table rows for each created product × each selected group
      if (groupIds.length > 0 && inserted && inserted.length > 0) {
        const relations = inserted.flatMap((prod: { id: string }) =>
          groupIds.map(gid => ({ product_id: prod.id, group_id: gid }))
        );
        const { error: relErr } = await supabase
          .from('inventory_product_group_relations')
          .insert(relations);
        if (relErr) throw relErr;
      }

      onCreated(rows.length);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.toLowerCase().includes('unique')) {
        setSaveError('אחד או יותר ממקטות הוריאציות כבר קיים בחברה זו. ערוך את המקטות בתצוגה המקדימה.');
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────

  const attrColHeaders = attrRows.map(r => r.typeName);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      dir="rtl"
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col w-full"
        style={{ maxWidth: step === 2 ? 900 : 540, maxHeight: '92vh' }}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-orange-100 rounded-xl flex-shrink-0">
              <Layers className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 leading-tight">
                אשף יצירת וריאציות
              </h2>
              <StepIndicator step={step} />
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors flex-shrink-0 disabled:opacity-40"
            aria-label="סגור"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* ════════════════════════════════════════════════════════════════
              STEP 1 — Base Details
              ════════════════════════════════════════════════════════════════ */}
          {step === 0 && (
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-500">
                הגדר את הפרטים הבסיסיים שיחולקו על פני כל הוריאציות.
              </p>

              {step1Error && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{step1Error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Base Name */}
                <div className="col-span-2 space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    שם בסיס <span className="text-red-500">*</span>
                    <span className="mr-1.5 text-xs font-normal text-gray-400">
                      (יצורף לכל וריאציה)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={baseName}
                    onChange={e => setBaseName(e.target.value)}
                    placeholder='לדוגמה: מכנסי אלי'
                    autoFocus
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>

                {/* Base SKU — only shown in 'smart' formula mode */}
                {isSequential ? (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">מקטות</label>
                    <div className="px-3 py-2.5 border border-dashed border-orange-300 rounded-lg
                                    bg-orange-50 text-sm text-orange-600">
                      יוקצו אוטומטית (מספור רץ)
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      מקט בסיס <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={baseSku}
                      onChange={e => setBaseSku(e.target.value.toUpperCase())}
                      placeholder="ELI-PANTS"
                      dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                                 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Base Price */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    מחיר בסיס (₪)
                    <span className="mr-1.5 text-xs font-normal text-gray-400">ניתן לשנות בתצוגה</span>
                  </label>
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">₪</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={basePrice}
                      onChange={e => setBasePrice(parseFloat(e.target.value) || 0)}
                      dir="ltr"
                      className="w-full pr-7 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Product Groups — multi-select with chips */}
                <div className="col-span-2 space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <Folder className="w-3.5 h-3.5 text-amber-500" />
                    קבוצות מוצר
                    <span className="text-xs font-normal text-gray-400">(עד {MAX_GROUPS_WIZARD} קבוצות)</span>
                  </label>

                  {/* Chips of selected groups */}
                  {groupIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {groupIds.map(gid => {
                        const grp = groupOptions.find(o => o.id === gid);
                        return (
                          <span
                            key={gid}
                            className="inline-flex items-center gap-1 pl-1.5 pr-2.5 py-0.5
                                       bg-amber-100 text-amber-800 text-xs font-medium rounded-full"
                          >
                            <button
                              type="button"
                              onClick={() => setGroupIds(prev => prev.filter(id => id !== gid))}
                              className="w-3.5 h-3.5 rounded-full flex items-center justify-center
                                         hover:bg-amber-300 transition-colors"
                              title="הסר קבוצה"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                            {grp?.label ?? gid}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Dropdown to add more groups */}
                  {groupIds.length < MAX_GROUPS_WIZARD && (
                    <div className="relative">
                      <select
                        value=""
                        onChange={e => {
                          const val = e.target.value;
                          if (!val || groupIds.includes(val)) return;
                          if (groupIds.length >= MAX_GROUPS_WIZARD) return;
                          setGroupIds(prev => [...prev, val]);
                        }}
                        className="w-full appearance-none px-3 py-2.5 border border-gray-300
                                   rounded-lg text-sm bg-white
                                   focus:outline-none focus:ring-2 focus:ring-orange-400
                                   focus:border-transparent pr-3 pl-8"
                      >
                        <option value="">— הוסף קבוצה —</option>
                        {groupOptions
                          .filter(opt => !groupIds.includes(opt.id))
                          .map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                          ))}
                      </select>
                      <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2
                                             w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  )}

                  {groupIds.length >= MAX_GROUPS_WIZARD && (
                    <p className="text-xs text-amber-600">הגעת למקסימום {MAX_GROUPS_WIZARD} קבוצות</p>
                  )}
                </div>

                {/* Cost (supplier purchase cost / COGS) */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <TrendingDown className="w-3.5 h-3.5 text-emerald-600" />
                    עלות מוצר (₪)
                    <span className="text-xs font-normal text-gray-400">(עלות קנייה)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">₪</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={baseCost ?? ''}
                      onChange={e => setBaseCost(e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      dir="ltr"
                      className="w-full pr-7 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-400">שינוי בעלות יירשם אוטומטית</p>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              STEP 2 — Attributes
              ════════════════════════════════════════════════════════════════ */}
          {step === 1 && (
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-gray-500">
                בחר סוגי וריאציה והוסף את הערכים עבור כל אחד. הערכים מופרדים בפסיקים.
              </p>

              {step2Error && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{step2Error}
                </div>
              )}

              {/* Available types */}
              {loadingTypes ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  טוען סוגי וריאציות…
                </div>
              ) : loadTypesError ? (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />{loadTypesError}
                </div>
              ) : varTypes.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  לא הוגדרו סוגי וריאציות עדיין.
                  עבור להגדרות מלאי → וריאציות כדי ליצור אותן.
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Chips for adding types */}
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">סוגי וריאציות זמינים</p>
                  <div className="flex flex-wrap gap-2">
                    {varTypes.map(t => {
                      const added = attrRows.some(r => r.typeId === t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => !added && addAttrRow(t)}
                          disabled={added}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all
                            ${added
                              ? 'bg-orange-100 text-orange-700 border-orange-200 cursor-default'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 cursor-pointer'
                            }`}
                        >
                          {added
                            ? <CheckCircle2 className="w-3.5 h-3.5" />
                            : <Plus className="w-3.5 h-3.5" />
                          }
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Configured attribute rows */}
              {attrRows.length > 0 && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    מאפיינים שנבחרו ({attrRows.length})
                  </p>
                  {attrRows.map((row, idx) => (
                    <div
                      key={row.typeId}
                      className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-xl p-3"
                    >
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5 pt-0.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => moveAttr(row.typeId, -1)}
                          disabled={idx === 0}
                          className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 transition-colors"
                          aria-label="הזז מעלה"
                        >
                          <ChevronRight className="w-3 h-3 -rotate-90" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveAttr(row.typeId, 1)}
                          disabled={idx === attrRows.length - 1}
                          className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 transition-colors"
                          aria-label="הזז מטה"
                        >
                          <ChevronLeft className="w-3 h-3 -rotate-90" />
                        </button>
                      </div>

                      {/* Type label */}
                      <div className="w-20 flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-800">{row.typeName}</span>
                      </div>

                      {/* Values input */}
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={row.rawInput}
                          onChange={e => updateAttrInput(row.typeId, e.target.value)}
                          placeholder="לדוגמה: אדום, ירוק, כחול"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                     focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                        {/* Value pills preview */}
                        {parseValues(row.rawInput).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {parseValues(row.rawInput).map((v, vi) => (
                              <span
                                key={vi}
                                className="inline-block px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-full border border-orange-200"
                              >
                                {v}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => removeAttrRow(row.typeId)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        aria-label={`הסר ${row.typeName}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Combination count preview */}
                  {generatedRows.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                      <Layers className="w-4 h-4 flex-shrink-0" />
                      <span>
                        יש ליצור{' '}
                        <strong>{generatedRows.length}</strong>
                        {' '}וריאציות (
                        {attrRows.map(r => parseValues(r.rawInput).length).join(' × ')}
                        )
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              STEP 3 — Preview & Edit
              ════════════════════════════════════════════════════════════════ */}
          {step === 2 && (
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-gray-500">
                  {isSequential
                    ? 'סקור את הוריאציות שיווצרו. מקטות יוקצו אוטומטית בעת היצירה.'
                    : 'סקור את הוריאציות שיווצרו. ניתן לערוך שם, מקט ומחיר לפני יצירה.'}
                </p>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-sm font-semibold text-orange-700 flex-shrink-0">
                  <Layers className="w-3.5 h-3.5" />
                  {previewRows.length} וריאציות
                </span>
              </div>

              {saveError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{saveError}
                </div>
              )}

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2.5 text-right whitespace-nowrap w-6">#</th>
                      {/* Attribute columns (read-only combo display) */}
                      {attrColHeaders.map(h => (
                        <th key={h} className="px-3 py-2.5 text-right whitespace-nowrap">{h}</th>
                      ))}
                      <th className="px-3 py-2.5 text-right min-w-[180px]">שם מוצר</th>
                      <th className="px-3 py-2.5 text-right min-w-[130px]">
                        {isSequential ? 'מקט (אוטו)' : 'מקט'}
                      </th>
                      <th className="px-3 py-2.5 text-right min-w-[90px]">מחיר ₪</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, idx) => (
                      <tr
                        key={row._key}
                        className={idx % 2 === 1 ? 'bg-gray-50/40' : 'bg-white'}
                      >
                        <td className="px-3 py-2 text-gray-400 text-xs tabular-nums">{idx + 1}</td>

                        {/* Attribute value cells (display only) */}
                        {row.combo.map((val, ci) => (
                          <td key={ci} className="px-3 py-2 whitespace-nowrap">
                            <span className="inline-block px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-full border border-orange-200">
                              {val}
                            </span>
                          </td>
                        ))}

                        {/* Editable: Name */}
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            value={row.name}
                            onChange={e => updatePreviewField(row._key, 'name', e.target.value)}
                            className="w-full px-2 py-1 border border-transparent rounded-md text-sm text-gray-900
                                       hover:border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                       bg-transparent focus:bg-white transition-all"
                          />
                        </td>

                        {/* SKU: auto-badge in sequential mode, editable in smart mode */}
                        <td className="px-2 py-1.5">
                          {isSequential ? (
                            <span className="inline-block px-2 py-1 bg-orange-50 border border-dashed
                                             border-orange-300 text-orange-600 text-xs font-mono rounded-md">
                              אוטו
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={row.sku}
                              onChange={e => updatePreviewField(row._key, 'sku', e.target.value)}
                              dir="ltr"
                              className="w-full px-2 py-1 border border-transparent rounded-md text-sm font-mono text-gray-700
                                         hover:border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                         bg-transparent focus:bg-white transition-all"
                            />
                          )}
                        </td>

                        {/* Editable: Price */}
                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.price}
                            onChange={e => updatePreviewField(row._key, 'price', e.target.value)}
                            dir="ltr"
                            className="w-full px-2 py-1 border border-transparent rounded-md text-sm text-gray-700 tabular-nums
                                       hover:border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                       bg-transparent focus:bg-white transition-all"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>{/* end body */}

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl flex-shrink-0">

          {/* Back / Cancel */}
          <div>
            {step === 0 ? (
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 bg-white rounded-lg transition-colors disabled:opacity-40"
              >
                ביטול
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep(s => s - 1)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 bg-white rounded-lg transition-colors disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
                חזור
              </button>
            )}
          </div>

          {/* Forward / Create */}
          <div>
            {step === 0 && (
              <button
                type="button"
                onClick={goToStep2}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                הבא
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            {step === 1 && (
              <button
                type="button"
                onClick={goToStep3}
                disabled={generatedRows.length === 0}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-40 disabled:cursor-not-allowed
                           text-white text-sm font-semibold rounded-lg transition-colors"
              >
                תצוגה מקדימה ({generatedRows.length})
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            {step === 2 && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving || previewRows.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-40 disabled:cursor-not-allowed
                           text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-500"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />יוצר…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4" />צור {previewRows.length} מוצרים</>
                )}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
